import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { api, errorMessage } from "../../lib/api";
import { ROLES, SERVICES } from "../../data/content";
import { Field, TextInput, TextArea, Choice, Honeypot, FormStatus } from "./Fields";

const OPTIONS = [...SERVICES.map((s) => s.title), "Guest speaking"];
const EMPTY = { name: "", email: "", organization: "", role: "", services: [], delivery: "Not sure yet", location: "", participants: "", timeframe: "", details: "", website: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggle = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role || form.services.length === 0) {
      setStatus({ type: "error", title: "A little more detail, please", text: "Choose your role and at least one service." });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await api.post("/quotes", form);
      setStatus({ type: "success", title: "Thank you, your quote request was sent.", text: `A quote will be sent to ${form.email}.` });
      toast.success("Quote request sent");
      setForm(EMPTY);
    } catch (err) {
      const msg = errorMessage(err);
      setStatus({ type: "error", title: "Your request wasn't sent", text: msg });
      toast.error("Request not sent", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Quote request" className="space-y-6" data-testid="quote-form">
      <Field id="q-name" label="Name" required><TextInput id="q-name" autoComplete="name" required value={form.name} onChange={set("name")} /></Field>
      <Field id="q-email" label="Email" required><TextInput id="q-email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} /></Field>
      <Field id="q-organization" label="Organization"><TextInput id="q-organization" autoComplete="organization" value={form.organization} onChange={set("organization")} /></Field>
      <Field id="q-role" label="Your role" required><Choice id="q-role" value={form.role} onChange={set("role")} options={ROLES} /></Field>
      <fieldset>
        <legend className="lbl text-ink">Services needed <span aria-hidden="true" className="text-teal">*</span></legend>
        <div className="mt-3 space-y-3 font-sans text-[15px]">
          {OPTIONS.map((s, i) => (
            <div key={s} className="flex items-start gap-3">
              <Checkbox id={`q-svc-${i}`} checked={form.services.includes(s)} onCheckedChange={() => toggle(s)} className="mt-0.5 border-ink data-[state=checked]:bg-teal data-[state=checked]:border-teal" data-testid={`quote-service-checkbox-${i}`} />
              <label htmlFor={`q-svc-${i}`} className="cursor-pointer">{s}</label>
            </div>
          ))}
        </div>
      </fieldset>
      <Field id="q-delivery" label="Preferred format" required><Choice id="q-delivery" value={form.delivery} onChange={set("delivery")} options={["In person", "Virtual", "Not sure yet"]} /></Field>
      <Field id="q-location" label="Location"><TextInput id="q-location" value={form.location} onChange={set("location")} /></Field>
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id="q-participants" label="Number of people"><TextInput id="q-participants" value={form.participants} onChange={set("participants")} /></Field>
        <Field id="q-timeframe" label="Timeframe"><TextInput id="q-timeframe" value={form.timeframe} onChange={set("timeframe")} /></Field>
      </div>
      <Field id="q-details" label="Project details"><TextArea id="q-details" rows={3} value={form.details} onChange={set("details")} /></Field>
      <Honeypot value={form.website} onChange={set("website")} />
      <button type="submit" disabled={busy} className="inline-flex items-center gap-2 font-sans border-2 border-teal text-teal rounded-full px-6 py-3 hover:bg-teal hover:text-paper transition-colors disabled:opacity-70" data-testid="quote-submit-button">
        {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        {busy ? "Sending..." : "Request a quote"}
      </button>
      <FormStatus status={status} testid="quote-form-status" />
    </form>
  );
};
